/**
 * Utility functions for IBAN validation.
 *
 * - Italian IBAN: IT + 2 check digits + 1 letter + 5 digits + 5 digits + 12 alphanumeric = 27 chars total
 * - Generic IBAN: 2 letter country code + 2 digits + 11-30 alphanumeric (total 15-34 chars)
 */

/** Normalizes an IBAN: removes spaces and uppercases. */
export function normalizeIban(value: string): string {
  return value.replace(/\s/g, '').toUpperCase();
}

/** Formats an Italian IBAN for display as 4, 6, 5 and 12 character groups. */
export function formatIban(value: string): string {
  const normalized = normalizeIban(value);
  const groups = [
    normalized.slice(0, 4),
    normalized.slice(4, 10),
    normalized.slice(10, 15),
    normalized.slice(15),
  ];

  return groups.filter(Boolean).join(' ');
}

/** Returns true when the value contains characters other than letters, digits or spaces. */
export function hasInvalidIbanCharacters(value: string): boolean {
  return /[^A-Z0-9\s]/i.test(value);
}

/** Returns true if the value matches the basic IBAN format (any country). */
export function isValidIbanFormat(value: string): boolean {
  const normalized = normalizeIban(value);
  return /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(normalized);
}

/** Returns true if the IBAN is a valid Italian IBAN (IT + 2 digits + 23 alphanumeric = 27 chars). */
export function isItalianIban(value: string): boolean {
  const normalized = normalizeIban(value);
  return /^IT[0-9]{2}[A-Z][0-9]{5}[0-9]{5}[A-Z0-9]{12}$/.test(normalized);
}

/** Returns true if the value is non-empty, passes basic IBAN format, but is NOT Italian. */
export function isForeignIban(value: string): boolean {
  const normalized = normalizeIban(value);
  return normalized.length > 0 && isValidIbanFormat(normalized) && !isItalianIban(normalized);
}

/** Returns true if the value is non-empty but does NOT pass even the basic IBAN format. */
export function isInvalidIbanFormat(value: string): boolean {
  const normalized = normalizeIban(value);
  return normalized.length > 0 && !isValidIbanFormat(normalized);
}
